<template>
  <BarChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import BarChart from '@/components/cards/models/BarChart';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import { POST_FIELDS, OPERATION, ENTITIES } from '@/services/charts-service';
import EventBus from '@/utils/event-bus';
import { mapActions, mapGetters } from 'vuex';
import highchartsBasics from '@/mixins/highcharts-basics';

export default {
  name: 'PublicationsByThemeBarChart',
  components: {
    BarChart,
  },
  mixins: [
    sentimentModel,
    cardsModel,
    highchartsBasics,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    field: {
      type: String,
      default: null,
    },
    group: {
      type: String,
      required: true,
    },
    entity: {
      type: String,
      default: ENTITIES.POSTS,
    },
    label: {
      type: String,
      default: null,
    },
    hideNull: {
      type: Boolean,
      default: false,
    },
    polarity: {
      type: Boolean,
      default: true,
    },
    stackLabels: {
      type: [Boolean, String],
      default: true,
    },
    filterName: {
      type: String,
      default: null,
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawData: [],
      pointsParser: 'points',
      filter: {},
      cardOptions: {
        yAxis: {
          stackLabels: {
            enabled: Boolean(this.stackLabels),
            ...(this.stackLabels === 'percentage'
              ? { formatter: (component => {
                return function() {
                  return `${component.sumSeries
                    ? ((this.total / component.sumSeries) * 100).toFixed(1)
                    : 0}%`;
                };
              })(this) }
              : null),
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['activeDashboardFilters', 'activeGlobalFilter', 'activeDashboard']),
    series() {
      const filteredValues = this.filter[this.filterName]?.value;
      const { pointsParser } = this;
      const data = [...this.rawData];

      let rawSeries = [];
      if (this.label) {
        data.forEach(p => {
          p.y = this.label;
          return p;
        });
      }
      if (filteredValues) {
        const filteredSeries = data?.filter(point => {
          return filteredValues.includes(point.x) || filteredValues.includes(point.y);
        }) || [];
        rawSeries = this.normalizeSentimentResponseFromChartService(filteredSeries, pointsParser);
      }
      rawSeries = this.normalizeSentimentResponseFromChartService(data, pointsParser, this.hideNull);

      if (!this.isExpandedCard) {
        return this.normalizeSeries(rawSeries);
      }
      return rawSeries;
    },
    sumSeries() {
      return this.series.reduce((sum, serie) => {
        return sum + serie.data.reduce((sum, point) => sum + point.y, 0);
      }, 0);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const grouping = [this.group];
      if (this.polarity && !this.field) {
        grouping.push(POST_FIELDS.POLARITY);
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getAggregatedData',
        points_parser: this.pointsParser,
        entity: this.entity,
        params: {
          grouping,
          operators: this.operation,
          ...(this.field ? { fields: [this.field] } : null),
          datePortion: this.getDatePortion(options),
        },
      })
        .then((response) => {
          this.rawData = this.extractData(response?.points) || [];
          this.requestStatus = 'success';

          if (this.$attrs.filter) {
            this.filter = this.$attrs.filter;
          }

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch((err) => {
          console.error(err);
          this.requestStatus = 'error';
        });
    },
    extractData(rawData) {
      const filterValues = this.$attrs.filter?.themes?.value || this.activeGlobalFilter?.themes?.value;
      let filteredData = rawData;
      if (filterValues) {
        filteredData = rawData.filter(item => filterValues.includes(item.x));
      }
      return filteredData;
    },
    normalizeSeries(series) {
      const filteredValues = this.filter[this.filterName]?.value;

      if (filteredValues) {
        return series.map(serie => {
          const filteredData = serie.data.filter(point => {
            const match = filteredValues.includes(point.x) || filteredValues.includes(point.y);
            return match;
          });

          if (!filteredData.length) {
            return { ...serie, data: serie.data };
          }

          return {
            ...serie,
            data: filteredData,
          };
        });
      }

      if (series.length === 1) {
        series[0].data.sort((first, second) => first.x - second.x);

        return [{
          ...series[0],
          data: series[0].data?.slice(0, this.maxItemsToBeDisplayed),
        }];
      }

      return series.map(serie => {
        const newData = serie.data.filter(each => each.x < this.maxItemsToBeDisplayed);

        if (!newData.length) {
          return { ...serie, data: serie.data };
        }

        return {
          ...serie,
          data: newData,
        };
      });
    },
  },
};
</script>
